import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Star } from "lucide-react";

interface RelatedListingsSectionProps {
  currentListingId: string;
  city: string;
  type: string;
}

export const RelatedListingsSection = ({ currentListingId, city, type }: RelatedListingsSectionProps) => {
  const navigate = useNavigate();
  const [relatedListings, setRelatedListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRelatedListings = async () => {
      try {
        // First try: same city (any type)
        const { data, error } = await supabase
          .from('listings')
          .select('id, name, slug, city, type, thumbnail_url, rating, is_premium')
          .eq('city', city)
          .eq('status', 'active')
          .neq('id', currentListingId)
          .limit(3);

        if (error) {
          console.error('Error fetching related listings:', error);
          setRelatedListings([]);
        } else {
          let listings = data || [];
          console.log('Related listings found:', listings.length, 'for city:', city);

          // Second try: if less than 3, get from any city
          if (listings.length < 3) {
            const { data: moreData } = await supabase
              .from('listings')
              .select('id, name, slug, city, type, thumbnail_url, rating, is_premium')
              .eq('status', 'active')
              .neq('id', currentListingId)
              .limit(3 - listings.length);
            
            listings = [...listings, ...(moreData || [])];
            console.log('After fetching more, total listings:', listings.length);
          }

          setRelatedListings(listings);
        }
      } catch (err) {
        console.error('Exception fetching related listings:', err);
        setRelatedListings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedListings();
  }, [currentListingId, city]);

  return (
    <Card>
      <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-6 border-b">
        <h2 className="text-2xl font-bold">Más en {city}</h2>
      </div>
      <CardContent className="p-6">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-muted aspect-video rounded-lg mb-3" />
                <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : relatedListings.length === 0 ? (
          <div className="text-muted-foreground">No hay listados relacionados disponibles ahora mismo.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" role="list">
            {relatedListings.slice(0, 3).map((listing) => (
            <div
              key={listing.id}
              onClick={() => navigate(`/listing/${listing.slug}`)}
              className="group cursor-pointer"
            >
              <div className="relative aspect-video rounded-lg overflow-hidden mb-3 shadow-md">
                <img
                  src={listing.thumbnail_url || '/default-listing-fallback.png'}
                  alt={listing.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  width="400"
                  height="225"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.src = '/default-listing-fallback.png'; }}
                />
                {listing.is_premium && (
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-primary text-white">Premium</Badge>
                  </div>
                )}
                <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-background/90 backdrop-blur px-2 py-1 rounded">
                  <Star className="w-3 h-3 fill-primary text-primary" />
                  <span className="text-sm font-semibold">{listing.rating || 0}</span>
                </div>
              </div>
              <h3 className="font-semibold truncate mb-1 group-hover:text-primary transition-colors">
                {listing.name}
              </h3>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <MapPin className="w-3 h-3" />
                <span className="truncate">{listing.city}</span>
              </div>
              <Badge variant="outline" className="mt-2 text-xs">
                {listing.type}
              </Badge>
            </div>
          ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};